import { ExperienceHistogram, ExperienceOptions } from './topicStrategies/experienceStrategy';
import { CompanyStageOptions } from './topicStrategies/companyStageStrategy';
import TitleDistribution from '../components/TitleDistribution';
import { TitleOptions } from './topicStrategies/title/components';
import { LocationKeysChips } from './topicStrategies/location/components';
import { BusinessModelOptions } from './topicStrategies/businessModelStrategy';
import SkillsConfirmation from '../components/SkillsConfirmation';

/**
 * Determine if a custom client-side component should be shown based on the decision
 * @param {Object} decision - Decision object from processUserMessage
 * @returns {Object} Component decision with type and props
 */
export const getCustomComponent = (decision) => {
  // Default is no custom component
  let componentDecision = {
    showCustomComponent: false,
    componentType: null,
    componentProps: null
  };
  
  // Check if we should show a custom component based on the action type
  if (decision.actionType === 'RENDER_TITLE_DISTRIBUTION') {
    // Show the title distribution component directly from the decision
    console.log('topicDecisionComponents: Rendering title distribution with data:', 
      decision.matchesAnalyzeResult?.analysis_info?.title_map);
    
    // Extract title_map and titleKeys from matchesAnalyzeResult
    const titleMap = decision.matchesAnalyzeResult?.analysis_info?.title_map || {};
    
    // Sort title keys by the number of profiles (array length) in descending order
    const titleKeys = Object.keys(titleMap)
      .sort((a, b) => {
        const aProfiles = Array.isArray(titleMap[a]) ? titleMap[a].length : 0;
        const bProfiles = Array.isArray(titleMap[b]) ? titleMap[b].length : 0;
        return bProfiles - aProfiles; // Sort in descending order
      });
    
    // Make sure we have the title distribution data
    if (!titleMap || Object.keys(titleMap).length === 0) {
      console.warn('topicDecisionComponents: Missing title_map data in decision:', decision);
    } else {
      console.log('topicDecisionComponents: Found', titleKeys.length, 'titles, top 5:', titleKeys.slice(0, 5));
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || TitleDistribution,
      componentProps: decision.componentProps || {
        titleMap,
        titleKeys,
        matchesAnalyze: decision.matchesAnalyzeResult, // Keep for backward compatibility
        onConfirm: decision.onConfirm,
        onReject: decision.onReject,
        onSendText: decision.onSendText,
        advanceToNextTopic: decision.advanceToNextTopic
      }
    };
    
    console.log('topicDecisionComponents: Title distribution component decision:', componentDecision);
  }
  else if (decision.actionType === 'RENDER_TITLE_OPTIONS') {
    // Show the title options component directly from the decision
    console.log('topicDecisionComponents: Rendering title options with data:', 
      decision.componentProps?.titleKeys);
    
    // Make sure we have the title keys data
    if (!decision.componentProps?.titleKeys || decision.componentProps.titleKeys.length === 0) {
      console.warn('topicDecisionComponents: Missing titleKeys data in decision:', decision);
    } else {
      console.log('topicDecisionComponents: Found', decision.componentProps.titleKeys.length, 'title keys');
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || TitleOptions,
      componentProps: decision.componentProps || {
        titleKeys: [],
        onTitlesSelected: () => {
          console.log('Titles selected');
        }
      }
    };
    
    console.log('topicDecisionComponents: Title options component decision:', componentDecision);
  }
  else if (decision.actionType === 'RENDER_EXPERIENCE_OPTIONS' || decision.actionType === 'RENDER_EXPERIENCE_HISTOGRAM') {
    // Show the experience options component directly from the decision
    console.log('topicDecisionComponents: Rendering experience options with data:', 
      decision.aiMatchesResult?.experienceHistogram);
    
    // Make sure we have the histogram data
    if (!decision.aiMatchesResult?.experienceHistogram) {
      console.warn('topicDecisionComponents: Missing experienceHistogram data in decision:', decision);
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || (decision.actionType === 'RENDER_EXPERIENCE_OPTIONS' ? ExperienceOptions : ExperienceHistogram),
      componentProps: decision.componentProps || {
        histogramData: decision.aiMatchesResult?.experienceHistogram,
        onRangeSelected: decision.onRangeSelected
      }
    };
    
    console.log('topicDecisionComponents: Component decision:', componentDecision);
  }
  // Maintain backward compatibility with the old way
  else if (decision.actionType === 'RENDER_LOCATION_KEYS') {
    // Show the location keys component
    console.log('topicDecisionComponents: Rendering location keys with data:', 
      decision.locationData);
    
    // Make sure we have the location data
    if (!decision.locationData || !decision.locationData.locationMap) {
      console.warn('topicDecisionComponents: Missing locationMap data in decision:', decision);
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || LocationKeysChips,
      componentProps: decision.componentProps || {
        locationMap: decision.locationData?.locationMap || {},
        locationKeys: decision.locationData?.locationKeys || [],
        onSendText: decision.onSendText,
        advanceToNextTopic: decision.advanceToNextTopic
      }
    };
    
    console.log('topicDecisionComponents: Location keys component decision:', componentDecision);
  }
  else if (decision.actionType === 'RENDER_COMPANY_STAGE_OPTIONS') {
    // Show the company stage options component
    console.log('topicDecisionComponents: Rendering company stage options with data:', 
      decision.aiMatchesResult?.companyStageData);
    
    // Make sure we have the company stage data
    if (!decision.aiMatchesResult?.companyStageData) {
      console.warn('topicDecisionComponents: Missing companyStageData in decision:', decision);
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || CompanyStageOptions,
      componentProps: decision.componentProps || {
        stageData: decision.aiMatchesResult?.companyStageData,
        onStagesSelected: decision.onStagesSelected,
        onSendText: decision.onSendText,
        advanceToNextTopic: decision.advanceToNextTopic
      }
    };
    
    console.log('topicDecisionComponents: Company stage options component decision:', componentDecision);
  }
  else if (decision.actionType === 'RENDER_COMPANY_BIZMODEL_OPTIONS') {
    // Show the business model options component
    console.log('topicDecisionComponents: Rendering business model options with data:', 
      decision.aiMatchesResult?.businessModelData);
    
    // Make sure we have the business model data
    if (!decision.aiMatchesResult?.businessModelData) {
      console.warn('topicDecisionComponents: Missing businessModelData in decision:', decision);
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || BusinessModelOptions,
      componentProps: decision.componentProps || {
        messages: decision.messages,
        setMessages: decision.setMessages,
        advanceToNextTopic: decision.advanceToNextTopic,
        handleRefreshResults: decision.handleRefreshResults,
        onSendText: decision.onSendText,
        matchResults: decision.matchResults
      }
    };
    
    console.log('topicDecisionComponents: Business model options component decision:', componentDecision);
  }
  else if (decision.actionType === 'RENDER_SKILLS_CONFIRMATION') {
    // Show the skills confirmation component
    console.log('topicDecisionComponents: Rendering skills confirmation with data:', 
      decision.componentProps?.skills);
    
    // Make sure we have the skills data
    if (!decision.componentProps?.skills || decision.componentProps.skills.length === 0) {
      console.warn('topicDecisionComponents: Missing skills data in decision:', decision);
    }
    
    componentDecision = {
      showCustomComponent: true,
      componentType: decision.component || SkillsConfirmation,
      componentProps: decision.componentProps || {
        skills: [],
        onConfirmed: () => {
          console.log('Skills confirmed');
        },
        onAddMore: () => {
          console.log('User wants to add more skills');
        }
      }
    };
    
    console.log('topicDecisionComponents: Skills confirmation component decision:', componentDecision);
  }
  else if (decision.actionType === 'SEND_WITH_GUIDANCE' && 
      decision.currentTopic === 'experience' && 
      decision.aiMatchesResult && 
      decision.aiMatchesResult.experienceHistogram) {
    
    console.log('topicDecisionComponents: Using backward compatibility for experience options with data:', 
      decision.aiMatchesResult.experienceHistogram);
    
    // Show experience options component
    componentDecision = {
      showCustomComponent: true,
      componentType: ExperienceOptions,
      componentProps: {
        histogramData: decision.aiMatchesResult.experienceHistogram, // Use histogramData instead of experienceHistogram
        requirements: decision.aiMatchesResult.search_gen_response?.icp_requirements || []
      }
    };
    
    console.log('topicDecisionComponents: Backward compatibility component decision:', componentDecision);
  }
  
  return componentDecision;
};

export default {
  getCustomComponent
};
